import { useContext, useEffect, useState } from "react";
import { useAnalytics } from "../../context/AnalyticsProvider";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

//CONTEXTS
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";

//COMPONENTS
import {
  OverallPerformance,
  TestHistoryList,
  EmptyHistory
} from "./components";

import CommonHeader from "../../components/common/CommonHeader";
import StatsGrid from "../../components/common/StatsGrid";
import { faClipboardCheck, faChartLine, faTrophy, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

//CONSTANTS
import { STRING, STRINGS } from "../../constant";

let _historyTracked = false;

function History() {
  const navigate = useNavigate();
  const { profile } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { track } = useAnalytics();

  // Track history view (once per page load)
  useEffect(() => {
    if (_historyTracked) return;
    _historyTracked = true;
    track('history_viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [historyData, setHistoryData] = useState([]);
  const [stats, setStats] = useState({
    totalTests: 0,
    avgScore: 0,
    totalQuestions: 0,
    totalCorrect: 0,
    avgAccuracy: 0,
    bestScore: 0,
    improvement: 0,
    avgTimePerTest: 0
  });

  useEffect(() => {
    processHistoryData();
  }, [profile]);

  const processHistoryData = () => {
    const testHistory = profile?.practice_test_history || [];
    
    if (testHistory.length === 0) {
      setHistoryData([]);
      return;
    }

    // Calculate statistics
    const correctArray = testHistory.map(e => parseInt(e?.correct || 0));
    const totalCorrect = _.sum(correctArray);
    
    const totalQuestionArray = testHistory.map(e => parseInt(e?.total || 0));
    const totalQuestions = _.sum(totalQuestionArray);
    
    const scoresArray = testHistory.map(e => parseInt(e?.score || 0));
    const totalScore = _.sum(scoresArray);
    const avgScore = Math.round(totalScore / testHistory.length) || 0;
    
    const avgAccuracy = totalQuestions > 0 
      ? Math.round((totalCorrect / totalQuestions) * 100) 
      : 0;
    
    const bestScore = Math.max(...scoresArray);
    
    // Calculate improvement (last test vs first test)
    const improvement = testHistory.length > 1 
      ? testHistory[testHistory.length - 1].score - testHistory[0].score 
      : 0;
    
    // Calculate average time per test
    const timeArray = testHistory.map(e => parseInt(e?.time_taken || 0));
    const totalTime = _.sum(timeArray);
    const avgTimePerTest = Math.round(totalTime / testHistory.length) || 0;

    
    setStats({
      totalTests: testHistory.length,
      avgScore: avgScore,
      totalQuestions: totalQuestions,
      totalCorrect: totalCorrect,
      avgAccuracy: avgAccuracy,
      bestScore: bestScore,
      improvement: improvement,
      avgTimePerTest: avgTimePerTest
    });

    // Group by date
    const grouped = _.chain(testHistory)
      .groupBy("date")
      .map((value, key) => ({ 
        title: key, 
        data: value.reverse() 
      }))
      .value()
      .reverse();

    setHistoryData(grouped);
  };

  const calculateStats = () => {
    const statsData = [
      {
        icon: faClipboardCheck,
        stat: stats.totalTests,
        label: 'Tests Taken',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faChartLine,
        stat: `${stats.avgScore}%`,
        label: 'Average Score',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faTrophy,
        stat: `${stats.bestScore}%`,
        label: 'Best Score',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faCheckCircle,
        stat: `${stats.totalCorrect}/${stats.totalQuestions}`,
        label: 'Total Correct',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      }
    ];
    return statsData;
  };

  const handleTestClick = (testData) => {
    // Sanitize the data to remove non-serializable properties
    const sanitizedData = {
      ...testData,
      date: testData.date ? testData.date.format('YYYY-MM-DD HH:mm:ss') : null,
      // Remove any other non-serializable properties if they exist
    };
    
    navigate("/testReview", { 
      state: { 
        historyData: sanitizedData, 
        canGoBackKey: false 
      } 
    });
  };

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        {/* Header */}
        
        <CommonHeader
          title="Test History"
          subtitle="Track your progress and review past performance"
          onBack={() => navigate("/home")}
          theme={theme}
          showBackButton={true}
        />
        
        {historyData.length > 0 ? (
          <>
            {/* Performance Insights */}
            
            <StatsGrid
              stats={calculateStats()}
              theme={theme}
            />
            
            
            {/* Main Grid */}
            <div className="grid grid-cols-1  gap-6 mt-6">
              {/* Overall Performance */}
              <div className="lg:col-span-1 lg:w-3/4 mx-auto">
                <OverallPerformance 
                  avgScore={stats.avgScore}
                  totalTests={stats.totalTests}
                  improvement={stats.improvement}
                  theme={theme}
                />
              </div>
              
              {/* Test History List */}
              <div className="lg:col-span-2 w-full mx-auto">
                <TestHistoryList 
                  historyData={historyData}
                  onTestClick={handleTestClick}
                  theme={theme}
                />
              </div>
            </div>
          </>
        ) : (
          <EmptyHistory 
            message={STRING.no_record_test_histoy}
            theme={theme}
          />
        )}
      </div>
    </div>
  );
}

export default History;